import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { mainRoute } from '@src/utils/consts/routesMainMenu.const';

import PageContainer from '@components/commons/containerPage.common';

import { useAnalytics } from '@contexts/analyticsContext';

import {
  addRecentProperty,
  getRecentProperties,
} from './_utils/localStorageHelpers';
import LocalizedThemeProvider from './_utils/localizedThemeProvider';

/**
 * Renders a list of Google Analytics properties, allowing selection and navigation.
 * Can be used as a standalone page or within a modal.
 * @param {Object} props - Component props.
 * @param {function} [props.onPropertySelect] - Callback when a property is selected in modal mode.
 * @param {boolean} [props.isModal=false] - True if the component is rendered inside a modal.
 */
function PropertiesListPage({ onPropertySelect, isModal = false }) {
  const navigate = useNavigate();
  const {
    firstInit,
    setFirstInit,
    properties,
    loadingProperties = true,
    propertiesError = false,
    changeSelectedProperty,
    selectedProperty,
  } = useAnalytics();

  const [recentProperties, setRecentProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const breadcrumbItems = isModal
    ? null
    : [
        { label: 'MISC', path: '#' },
        { label: 'ANALYTICS', path: mainRoute.MISC_ANALYTICS },
      ];

  // Load recent properties from local storage on component mount
  useEffect(() => {
    setRecentProperties(getRecentProperties());
  }, []);

  useEffect(() => {
    if (firstInit === null) setFirstInit(true);
  }, [firstInit]);

  const handlePropertyClick = (property) => {
    // Add to recently selected list
    addRecentProperty(property);
    setRecentProperties(getRecentProperties());

    if (isModal && onPropertySelect) {
      // If in modal, use the callback to pass selection back to parent
      onPropertySelect(property);
    } else {
      // If not in modal, set in context and navigate
      changeSelectedProperty(property);
      navigate(`/analytics/${property.propertyId}`);
    }
  };

  // Filter properties based on search term
  const filteredProperties = properties.filter(
    (property) =>
      property.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.propertyId.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Filter recent properties based on search term
  const filteredRecentProperties = recentProperties.filter(
    (property) =>
      property.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.propertyId.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loadingProperties ?? true) {
    return (
      <PageContainer
        breadcrumbItems={breadcrumbItems}
        disableBreadcrumb={breadcrumbItems === null}
        childrenSx={{ p: 3 }}
      >
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography variant='h6' sx={{ ml: 2 }}>
            Loading Google Analytics Properties...
          </Typography>
          {/* ToastContainer is handled by parent App or ReportSnapshotPage */}
        </Box>
      </PageContainer>
    );
  }

  if (propertiesError ?? false) {
    return (
      <PageContainer
        breadcrumbItems={breadcrumbItems}
        disableBreadcrumb={breadcrumbItems === null}
        childrenSx={{ p: 3 }}
      >
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity='error'>
            Error loading properties: {propertiesError}
          </Alert>
          {/* ToastContainer is handled by parent App or ReportSnapshotPage */}
        </Box>
      </PageContainer>
    );
  }

  if (
    (!(loadingProperties ?? true) && !properties) ||
    (!(loadingProperties ?? true) && properties.length === 0)
  ) {
    return (
      <PageContainer
        breadcrumbItems={breadcrumbItems}
        disableBreadcrumb={breadcrumbItems === null}
        childrenSx={{ p: 3 }}
      >
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity='info'>
            No Google Analytics properties found for this account.
          </Alert>
          {/* ToastContainer is handled by parent App or ReportSnapshotPage */}
        </Box>
      </PageContainer>
    );
  }

  return (
    <LocalizedThemeProvider>
      <PageContainer
        breadcrumbItems={breadcrumbItems}
        disableBreadcrumb={breadcrumbItems === null}
        childrenSx={{ p: 3 }}
      >
        <Box maxWidth='md' sx={{ py: isModal ? 0 : 4 }}>
          {' '}
          {/* Adjust padding based on modal usage */}
          <Paper elevation={isModal ? 0 : 3} sx={{ p: isModal ? 0 : 3 }}>
            {' '}
            {/* Remove paper styling if in modal */}
            {!isModal && ( // Only show title and divider if not in modal
              <>
                <Typography
                  variant='h5'
                  component='h2'
                  gutterBottom
                  align='center'
                >
                  Select a Google Analytics Property
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </>
            )}
            {/* Search Bar */}
            <TextField
              fullWidth
              label='Search properties by name or ID'
              variant='outlined'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 3 }}
            />
            {/* Recently Viewed Properties Section */}
            {filteredRecentProperties.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant='h6' component='h3' gutterBottom>
                  Recently Viewed
                </Typography>
                <List dense>
                  {filteredRecentProperties.map((property) => (
                    <ListItem
                      button
                      key={property.propertyId}
                      onClick={() => handlePropertyClick(property)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor:
                          selectedProperty?.propertyId === property.propertyId
                            ? 'action.selected'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant='subtitle1' fontWeight='medium'>
                            {property.displayName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant='body2' color='text.secondary'>
                            ID: {property.propertyId} | Account:{' '}
                            {property.accountDisplayName}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ mt: 2 }} />
              </Box>
            )}
            {/* All Properties List */}
            <Typography
              variant='h6'
              component='h3'
              gutterBottom
              sx={{ mt: filteredRecentProperties.length > 0 ? 4 : 0 }}
            >
              All Properties
            </Typography>
            <List>
              {filteredProperties.map((property) => (
                <React.Fragment key={property.propertyId}>
                  <ListItem
                    button
                    onClick={() => handlePropertyClick(property)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor:
                        selectedProperty?.propertyId === property.propertyId
                          ? 'action.selected'
                          : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant='subtitle1' fontWeight='medium'>
                          {property.displayName}
                        </Typography>
                      }
                      secondary={
                        <Typography variant='body2' color='text.secondary'>
                          ID: {property.propertyId} | Account:{' '}
                          {property.accountDisplayName}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider component='li' sx={{ my: 1 }} />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      </PageContainer>
    </LocalizedThemeProvider>
  );
}

export default PropertiesListPage;
